import React, { useState, useEffect } from 'react';
import cx from 'classnames';
import {
  CountryCode,
  parsePhoneNumber,
  dropdownItems,
  isSupportedCountry,
} from 'lane-shared/helpers/phoneNumbers';
import { getCountryCallingCode } from 'libphonenumber-js';
import { Dropdown, TagInput, Flex } from 'design-system-web';
import { S } from 'components/typography';
import styles from './VisitorPhoneTagInput.scss';

type Props = {
  className?: string;
  style?: React.CSSProperties;
  displayName?: string;
  placeholder: string;
  tags: string[];
  setTags: (tags: string[]) => void;
  validation?: (tag: string) => boolean;
  errorText?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  regionCode?: CountryCode;
  onRegionChange?: (regionCode: CountryCode) => void;
};

export default function VisitorPhoneTagInput({
  className,
  style,
  displayName,
  placeholder,
  tags,
  setTags,
  validation,
  errorText,
  isRequired = false,
  isDisabled = false,
  regionCode: propRegionCode,
  onRegionChange,
}: Props) {
  const browserLanguage = window.navigator.language;
  let browserRegion = '';
  
  try {
    browserRegion = new Intl.Locale(browserLanguage).region || '';
  } catch {
    browserRegion = '';
  }
  
  const defaultRegion = isSupportedCountry(browserRegion)
    ? (browserRegion as CountryCode)
    : 'US';
  
  const [regionCode, setRegionCode] = useState<CountryCode>(propRegionCode || defaultRegion);

  useEffect(() => {
    if (propRegionCode) {
      setRegionCode(propRegionCode);
    }
  }, [propRegionCode]);

  const addCountryCodeToPhone = (phone: string): string => {
    if (!phone || phone.trim() === '') {
      return phone;
    }

    if (phone.startsWith('+')) {
      return phone;
    }

    const countryCallingCode = getCountryCallingCode(regionCode);
    
    return `+${countryCallingCode}${phone}`;
  };

  const removeCountryCodeFromPhone = (phone: string): string => {
    if (!phone || phone.trim() === '') {
      return phone;
    }

    if (phone.startsWith('+')) {
      try {
        const parsed = parsePhoneNumber(phone);

        return parsed?.nationalNumber || phone;
      } catch {
        return phone;
      }
    }

    return phone;
  };

  const displayTags = (tags || []).map(removeCountryCodeFromPhone);

  const handleTagsChange = (newTags: string[]) => {
    const tagsWithCountryCode = newTags.map(addCountryCodeToPhone);

    setTags(tagsWithCountryCode);
  };

  const handleCountryChange = (newRegionCode: CountryCode) => {
    setRegionCode(newRegionCode);
    
    if (onRegionChange) {
      onRegionChange(newRegionCode);
    }
    
    const updatedTags = (tags || []).map(phone => {
      if (!phone || phone.trim() === '') {
        return phone;
      }

      if (phone.startsWith('+')) {
        try {
          const parsed = parsePhoneNumber(phone);

          if (parsed && parsed.isValid()) {
            return phone; // Keep the original phone with its country code
          }
        } catch {
          // If parsing fails, treat as invalid and apply new country code
        }
      }

      // Only apply new country code to national numbers (no + prefix)
      const nationalNumber = removeCountryCodeFromPhone(phone);
      const countryCallingCode = getCountryCallingCode(newRegionCode);
      
      return `+${countryCallingCode}${nationalNumber}`;
    });
    
    setTags(updatedTags);
  };

  const validatePhoneWithCountryCode = (tag: string): boolean => {
    if (!tag || tag.trim() === '') {
      return false;
    }

    const phoneWithCountryCode = addCountryCodeToPhone(tag);
    
    try {
      const parsedPhone = parsePhoneNumber(phoneWithCountryCode);

      return parsedPhone ? parsedPhone.isValid() : false;
    } catch {
      return false;
    }
  };

  return (
    <div className={cx(styles.visitorPhoneTagInput, className)} style={style}>
      <S>
        {displayName}
        {isRequired ? <span className={styles.required}>*</span> : ''}
      </S>
      <Flex className={styles.form}>
        <div className={styles.dropdownWrapper}>
          <Dropdown
            items={dropdownItems}
            placeholder="Country"
            value={regionCode}
            onChange={code => handleCountryChange(code.value)}
            className={styles.dropdown}
            disabled={isDisabled}
            testId="VisitorPhoneTagDropdown"
          />
        </div>
        <div className={styles.tagInputWrapper}>
          <TagInput
            placeholder={placeholder}
            tags={displayTags}
            setTags={handleTagsChange}
            validateTag={validation || validatePhoneWithCountryCode}
            errorText={errorText}
          />
        </div>
      </Flex>
    </div>
  );
}
